import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Map, MapCameraChangedEvent, useMap } from "@vis.gl/react-google-maps";
import ClusteredLocationMarkers from "./ClusteredLocationMarkers";
import type { Supplier } from "../../../types/Supplier";
import { useSuppressResizeObserverErrors } from "../../../hooks/useSuppressResizeObserverErrors";
import DatasetLayer from "./DatasetLayer";
import './LocationMap.css';
import {useDefaultMapZoom} from "../../../hooks/maps/useDefaultMapZoom";

const BOUNDS_CHANGE_DEBOUNCE_MS = 150;

// Total time to suppress bounds propagation after a pin click.
// Covers the centring pan animation (~300ms) + margin.
const AUTO_PAN_SETTLE_MS = 700;

interface ILocationMapProps {
  onLocationSelect: (location: any) => void,
  locations?: Supplier[],
  listHover: Supplier | null,
  defaultCenter?: { lat: number, lng: number },
  defaultZoom?: number,
  onBoundsChanged?: (bounds: google.maps.LatLngBounds | undefined) => void,
  /** Fired when the user starts dragging the map (not on programmatic recenter). */
  onUserDragStart?: () => void
}

// Used when the card isn't measurable yet; roughly the rendered card height.
const FALLBACK_CARD_HEIGHT_PX = 400;

/**
 * Centres the highlighted pin and its info-card (which renders above the pin) in the map.
 * Replaces the InfoWindow's native auto-pan, which only nudges the card into view and can
 * push the pin past the map edge, dropping it from the list. Must live inside <Map> to use useMap().
 */
const MapPanController: FC<{ highlightedLocation: Supplier | null }> = ({ highlightedLocation }) => {
  const map = useMap();
  const prevLocationIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!map || !highlightedLocation) {
      prevLocationIdRef.current = null;
      return;
    }

    // Only pan when a new location is highlighted (not on re-renders)
    if (prevLocationIdRef.current === highlightedLocation.locationId) return;
    prevLocationIdRef.current = highlightedLocation.locationId;

    // Wait a frame so the InfoWindow is laid out and its height is measurable
    const frameId = requestAnimationFrame(() => {
      const projection = map.getProjection();
      const zoom = map.getZoom();
      const pin = projection?.fromLatLngToPoint({
        lat: parseFloat(highlightedLocation.addressLatitude),
        lng: parseFloat(highlightedLocation.addressLongitude),
      });
      if (!projection || !pin || zoom === undefined) return;

      const card = map.getDiv().querySelector<HTMLElement>('.map-preview-wrapper');
      const cardHeight = card?.offsetHeight || FALLBACK_CARD_HEIGHT_PX;
      // Aim half the card height above the pin. World coords span 0-256 (zoom-0 pixels) and
      // pixel = world * 2^zoom, so px -> world is px / 2^zoom (no extra 256 factor).
      // https://developers.google.com/maps/documentation/javascript/coordinates#pixel-coordinates
      const offsetY = cardHeight / 2 / 2 ** zoom;
      const target = projection.fromPointToLatLng(new google.maps.Point(pin.x, pin.y - offsetY));
      if (target) map.panTo(target);
    });

    return () => cancelAnimationFrame(frameId);
  }, [map, highlightedLocation]);

  return null;
};

/**
 * Component that reports initial map bounds when the map loads.
 * Must be rendered inside the Map component to access the map instance.
 */
const MapBoundsReporter: FC<{
  onBoundsChanged?: (bounds: google.maps.LatLngBounds | undefined) => void;
  defaultCenter?: { lat: number, lng: number };
}> = ({ onBoundsChanged, defaultCenter }) => {
  const map = useMap();
  const prevCenterRef = useRef(defaultCenter);

  useEffect(() => {
    if (map && onBoundsChanged) {
      // Check if center changed (new search) or this is initial load
      const centerChanged = prevCenterRef.current?.lat !== defaultCenter?.lat ||
                           prevCenterRef.current?.lng !== defaultCenter?.lng;

      if (centerChanged || !prevCenterRef.current) {
        // Wait for map to recenter and calculate bounds
        const timeoutId = setTimeout(() => {
          const bounds = map.getBounds();
          if (bounds) {
            onBoundsChanged(bounds);
            prevCenterRef.current = defaultCenter;
          }
        }, 150); // Slightly longer to ensure map has recentered

        return () => clearTimeout(timeoutId);
      }
    }
  }, [map, onBoundsChanged, defaultCenter]);

  return null;
};

const LocationMap: FC<ILocationMapProps> = (props) => {
  const { defaultCenter, defaultZoom, onBoundsChanged, onLocationSelect, locations = [], listHover, onUserDragStart } = props;
  const [center, setCenter] = useState(defaultCenter);
  const defaultMapZoom = useDefaultMapZoom();
  const initialZoom = defaultZoom ?? defaultMapZoom;
  const [zoom, setZoom] = useState(initialZoom);
  const [highlightedLocation, setHighlightedLocation] = useState<Supplier | null>(null);
  const prevDefaultCenterRef = useRef(defaultCenter);
  const boundsChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Queue bounds (and note zoom) during auto-pan instead of re-filtering the list mid-animation
  const pendingBoundsRef = useRef<google.maps.LatLngBounds | undefined>(undefined);
  const pendingZoomRef = useRef<number | null>(null);

  // Track the InfoWindow auto-pan window to prevent flicker
  // When a location is highlighted, we defer list re-filtering for AUTO_PAN_SETTLE_MS
  // so the expensive filter doesn't run mid-animation
  const infoWindowPanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInfoWindowAutoPanningRef = useRef(false);
  const zoomAtHighlightRef = useRef(zoom);
  // Always reflects the latest zoom value (updated on every render).
  // Read inside effects to avoid adding `zoom` as a dependency.
  const latestZoomRef = useRef(zoom);
  latestZoomRef.current = zoom;

  // Suppress ResizeObserver errors (common with Google Maps, harmless)
  useSuppressResizeObserverErrors();

  // Update center and zoom when defaultCenter prop changes (new search location)
  useEffect(() => {
    if (defaultCenter) {
      // Check if this is actually a new search (different coordinates)
      const isNewSearch = prevDefaultCenterRef.current?.lat !== defaultCenter.lat ||
        prevDefaultCenterRef.current?.lng !== defaultCenter.lng;

      setCenter(defaultCenter);
      setZoom(defaultZoom ?? defaultMapZoom); // Reset zoom

      // Only clear highlighted location on genuine new search
      if (isNewSearch) {
        setHighlightedLocation(null);
        prevDefaultCenterRef.current = defaultCenter;
      }
    }
  }, [defaultCenter, defaultMapZoom, defaultZoom]);

  // Manage the auto-pan window when highlight changes.
  // Suppresses ALL bounds propagation for AUTO_PAN_SETTLE_MS, then flushes the final
  // camera state once — preventing mid-animation list re-filters (stutter).
  useEffect(() => {
    const clearPendingRefs = () => {
      pendingBoundsRef.current = undefined;
      pendingZoomRef.current = null;
    };

    if (highlightedLocation) {
      // Capture zoom at highlight-time so the flush callback can detect
      // user-initiated zoom changes without adding zoom to deps.
      zoomAtHighlightRef.current = latestZoomRef.current;

      // Start the auto-pan window when a location is highlighted
      isInfoWindowAutoPanningRef.current = true;

      // Cancel any pending debounced bounds update so it doesn't fire mid-suppression
      if (boundsChangeTimeoutRef.current) {
        clearTimeout(boundsChangeTimeoutRef.current);
      }

      if (infoWindowPanTimeoutRef.current) {
        clearTimeout(infoWindowPanTimeoutRef.current);
      }

      // End the auto-pan window after the full animation sequence settles
      infoWindowPanTimeoutRef.current = setTimeout(() => {
        isInfoWindowAutoPanningRef.current = false;

        // If user scrolled-to-zoom during auto-pan, close the preview to avoid stale position
        const zoomChanged = pendingZoomRef.current !== null && pendingZoomRef.current !== zoomAtHighlightRef.current;
        if (zoomChanged) {
          setHighlightedLocation(null);
        }

        // Single bounds update → single list re-filter
        if (pendingBoundsRef.current && onBoundsChanged) {
          onBoundsChanged(pendingBoundsRef.current);
        }
        clearPendingRefs();
      }, AUTO_PAN_SETTLE_MS);

      return () => {
        // Cleanup when highlight is cleared before timer completes or on unmount
        isInfoWindowAutoPanningRef.current = false;
        if (infoWindowPanTimeoutRef.current) {
          clearTimeout(infoWindowPanTimeoutRef.current);
        }
        clearPendingRefs();
      };
    } else {
      // Also reset when highlightedLocation is cleared (e.g., user clicked again, zoomed, or dragged)
      isInfoWindowAutoPanningRef.current = false;
      if (infoWindowPanTimeoutRef.current) {
        clearTimeout(infoWindowPanTimeoutRef.current);
      }
      clearPendingRefs();
    }
  }, [highlightedLocation, onBoundsChanged]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (boundsChangeTimeoutRef.current) {
        clearTimeout(boundsChangeTimeoutRef.current);
      }
      if (infoWindowPanTimeoutRef.current) {
        clearTimeout(infoWindowPanTimeoutRef.current);
      }
    };
  }, []);

  const handleCameraChanged = (ev: MapCameraChangedEvent) => {
    // During auto-pan, only defer the list re-filter; the useEffect timer flushes it once
    // the animation settles. Center/zoom must stay in sync: <Map> is controlled, so a stale
    // `center` prop makes vis.gl snap the camera back and cancels the InfoWindow auto-pan.
    if (isInfoWindowAutoPanningRef.current) {
      if (ev.detail.center) {
        setCenter({
          lat: ev.detail.center.lat,
          lng: ev.detail.center.lng,
        });
      }
      if (ev.detail.zoom !== undefined) {
        pendingZoomRef.current = ev.detail.zoom;
        setZoom(ev.detail.zoom);
      }
      if (ev.type === 'bounds_changed') {
        pendingBoundsRef.current = ev.map.getBounds() ?? undefined;
      }
      return;
    }

    // Normal camera update handling after auto-pan completes or when no popup is open
    if (ev.detail.center) {
      setCenter({
        lat: ev.detail.center.lat,
        lng: ev.detail.center.lng
      });
    }
    if (ev.detail.zoom !== undefined) {
      // Close info window when zoom changes to prevent flicker
      if (ev.detail.zoom !== zoom) {
        setHighlightedLocation(null);
      }
      setZoom(ev.detail.zoom);
    }

    // Throttle bounds change to improve drag performance
    // Only call the expensive filtering operation after user stops dragging briefly
    if (ev.type === 'bounds_changed' && onBoundsChanged) {
      if (boundsChangeTimeoutRef.current) {
        clearTimeout(boundsChangeTimeoutRef.current);
      }
      boundsChangeTimeoutRef.current = setTimeout(() => {
        const bounds = ev.map.getBounds();
        if (bounds) {
          onBoundsChanged!(bounds);
        }
      }, BOUNDS_CHANGE_DEBOUNCE_MS);
    }
  };

  const handleClickEvent: () => void = useCallback(() => {
    //turn off highlightedId
    setHighlightedLocation(null);
  }, []);

  const handleDragStart: () => void = useCallback(() => {
    // Close info window when user starts dragging the map to prevent glitches
    setHighlightedLocation(null);
    // Signal a genuine user drag so the parent can switch to coordinate mode.
    onUserDragStart?.();
  }, [onUserDragStart]);

  return (
    <div className="location-map-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        mapId={import.meta.env.VITE_GOOGLE_MAPS_ID}
        center={center}
        zoom={zoom}
        mapTypeControl={true}
        streetViewControl={false}
        fullscreenControl={false}
        zoomControl={true}
        zoomControlOptions={{ position: 3 }} // TOP_RIGHT
        clickableIcons={false}
        onCameraChanged={handleCameraChanged}
        onClick={handleClickEvent}
        onDragstart={handleDragStart}
      >
        {/* Report initial bounds when map loads - critical for mobile hidden map */}
        <MapBoundsReporter
          onBoundsChanged={onBoundsChanged}
          defaultCenter={defaultCenter}
        />
        {/* Centre the pin and its info-card when a pin is clicked */}
        <MapPanController highlightedLocation={highlightedLocation} />
        <ClusteredLocationMarkers
          locations={locations}
          onClick={onLocationSelect}
          hoveredLocation={listHover}
          highlightedLocation={highlightedLocation}
          setHighlightedLocation={setHighlightedLocation}
        />
        {/* Truck Stop Parking Dataset */}
        <DatasetLayer
          datasetId={import.meta.env.VITE_TRUCK_STOP_DATASET_ID || "MISSING_TRUCK_STOP_DATASET_ID"}
          minZoom={defaultMapZoom}
          maxZoom={22}
        />
      </Map>
    </div>
  );
}

export default LocationMap;
